import {
    CalendarClock,
    CalendarDays,
    Clock,
    GraduationCap,
    ShieldCheck,
    Sparkles,
    UserPlus,
    UserCircle,
    Wallet,
} from "lucide-react";

/**
 * Domain metadata for the AI Copilot.
 *
 * Keys mirror the backend's ai.constants domains exactly — the chat response
 * returns `domain`, and the UI looks it up here for the badge label/icon/tint.
 * An unknown domain falls back to GENERAL rather than rendering blank.
 */
export const COPILOT_DOMAINS = {
    general: {
        label: "General",
        icon: Sparkles,
        tint: "bg-sidebar/10 text-sidebar-primary dark:bg-sidebar/20 dark:text-teal-300",
    },
    employee: {
        label: "Profile",
        icon: UserCircle,
        tint: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    },
    leave: {
        label: "Leave",
        icon: CalendarClock,
        tint: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
    },
    attendance: {
        label: "Attendance",
        icon: Clock,
        tint: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    },
    payroll: {
        label: "Payroll",
        icon: Wallet,
        tint: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    },
    recruitment: {
        label: "Recruitment",
        icon: UserPlus,
        tint: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
    },
    training: {
        label: "Training",
        icon: GraduationCap,
        tint: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
    },
    compliance: {
        label: "Compliance",
        icon: ShieldCheck,
        tint: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    },
    holiday: {
        label: "Holidays",
        icon: CalendarDays,
        tint: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
    },
};

export const getDomainMeta = (domain) => COPILOT_DOMAINS[domain] || COPILOT_DOMAINS.general;

/**
 * Suggested prompts.
 *
 * Deliberately phrased as QUESTIONS, not actions. The copilot is read-only — it
 * answers from HRMS data and cannot apply leave, download a PDF or update a
 * record — so suggesting "Apply 2 days casual leave" would advertise something
 * the backend will not do.
 *
 * `hrOnly` prompts are only shown to admin/HR callers, matching the backend,
 * which downgrades organisation-wide questions from a regular employee to their
 * own scope.
 */
export const COPILOT_SUGGESTIONS = [
    { text: "What is my leave balance?", hrOnly: false },
    { text: "Show my latest payslip summary", hrOnly: false },
    { text: "What is my attendance status today?", hrOnly: false },
    { text: "When is the next company holiday?", hrOnly: false },
    { text: "Do I have any pending training?", hrOnly: false },
    { text: "Am I missing any statutory documents?", hrOnly: false },
    { text: "Who is my reporting manager?", hrOnly: false },
    { text: "How many employees do we have?", hrOnly: true },
    { text: "How many candidates are in the pipeline?", hrOnly: true },
];

/** Compact prompt set for the docked widget, where horizontal space is tight. */
export const COPILOT_QUICK_PROMPTS = [
    "My leave balance",
    "Latest payslip",
    "Today's attendance",
    "Next holiday",
];

export const COPILOT_GREETING_HINT =
    "Ask about your leave balance, payslips, attendance, training, holidays or document compliance.";

/** Keys inside the response `data` payload that are structural, not metrics. */
export const NON_METRIC_KEYS = [
    "summary",
    "note",
    "restricted",
    "has_payslip",
    "declared",
    "lookahead_days",
    "documents_tracked",
    "leave_types",
    "pending_leave_approvals",
    "pending_attendance_corrections",
    "proactive_alerts",
    "navigation",
    "subordinates",
    "subordinates_count",
    "total_company_members",
    "is_target_employee",
    "email",
    "reporting_manager_id",
    "employee_id",
    "is_admin",
    "access_level",
];
